import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../components/AppContext';
import config from '../config';
import {
  Container,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Portal
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import AdaptersSection from '../components/sincro-config/AdaptersSection';
import AutoconcepteSection from '../components/sincro-config/AutoconcepteSection';
import AlarmesSection from '../components/sincro-config/AlarmesSection';
import {
  sanitizeSyncConfigPayload,
  findCorruptedEmptyFilters
} from '../functions/syncConfigSanitizer';

const SincroConfigPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { userapp: routeUserapp } = useParams();
  const navigate = useNavigate();
  const { ctxUser, ctxApp, setCtxApp, conceptesList } = useAppContext();
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';

  // Llista de perfils disponibles al backend
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [selectedUserapp, setSelectedUserapp] = useState(routeUserapp || ctxApp?.userapp || '');

  // Configuració activa i estat inicial per dirty check
  const [profileConfig, setProfileConfig] = useState(null);
  const [initialConfig, setInitialConfig] = useState(null);

  // Estats UI
  const [currentTab, setCurrentTab] = useState(0);
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modals de confirmació
  const [pendingUserappSwitch, setPendingUserappSwitch] = useState(null);
  const [openUnsavedDialog, setOpenUnsavedDialog] = useState(false);

  // Feedback Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // 1. Carregar arbre complet de configuracions (/api/v1/syncbanc/fdvconfig)
  const fetchAvailableProfiles = useCallback(async () => {
    if (!jwtToken) return;
    try {
      setIsLoadingTree(true);
      const headers = { Authorization: `${jwtToken}` };
      const res = await axios.get(`${config.apiBaseUrl}/v1/syncbanc/fdvconfig`, { headers });
      
      const userappsMap = res.data?.fdvsync?.userapps || {};
      const profiles = Object.keys(userappsMap).map((key) => ({
        key: key.toLowerCase(),
        nom: userappsMap[key]?.nom || key.toUpperCase(),
        enabled: userappsMap[key]?.enabled ?? true
      }));

      setAvailableProfiles(profiles);

      // Si no tenim userapp seleccionat o no existeix a la llista, agafem el primer
      if (!selectedUserapp && profiles.length > 0) {
        setSelectedUserapp(profiles[0].key);
      }
    } catch (err) {
      console.error('Error al carregar els perfils de sincronització:', err);
      // Fallback a comptes del context si falla la crida inicial
      if (ctxApp?.comptes && ctxApp.comptes.length > 0) {
        const fallback = ctxApp.comptes.map((c) => ({
          key: c.userapp.toLowerCase(),
          nom: c.userapp.toUpperCase(),
          enabled: true
        }));
        setAvailableProfiles(fallback);
        if (!selectedUserapp) setSelectedUserapp(fallback[0].key);
      }
    } finally {
      setIsLoadingTree(false);
    }
  }, [jwtToken, selectedUserapp, ctxApp?.comptes]);

  // 2. Carregar configuració del perfil seleccionat (/api/v1/syncbanc/fdvconfig/{userapp})
  const fetchProfileConfig = useCallback(async (userappKey) => {
    if (!userappKey || !jwtToken) return;
    try {
      setIsLoadingProfile(true);
      const headers = { Authorization: `${jwtToken}` };
      const res = await axios.get(`${config.apiBaseUrl}/v1/syncbanc/fdvconfig/${userappKey}`, { headers });
      
      const data = res.data || {};
      // Normalitzar regles-assignacio per assegurar que sempre tenim la propietat 'import'
      if (data.autoconcepte && data.autoconcepte['regles-assignacio']) {
        const rules = data.autoconcepte['regles-assignacio'];
        Object.keys(rules).forEach((k) => {
          if (rules[k] && rules[k].import === undefined && rules[k].importe !== undefined) {
            rules[k].import = rules[k].importe;
          }
        });
      }
      setProfileConfig(data);
      setInitialConfig(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      console.error(`Error al carregar configuració per a ${userappKey}:`, err);
      setSnackbar({
        open: true,
        message: `Error al carregar la configuració del perfil ${userappKey}`,
        severity: 'error'
      });
    } finally {
      setIsLoadingProfile(false);
    }
  }, [jwtToken]);

  // Inicialització perfils quan es disposa de sessió/token
  useEffect(() => {
    if (jwtToken) {
      fetchAvailableProfiles();
    }
  }, [fetchAvailableProfiles, jwtToken]);

  // Carregar configuració en canviar el perfil seleccionat o en disposar de jwtToken
  useEffect(() => {
    if (selectedUserapp && jwtToken) {
      fetchProfileConfig(selectedUserapp);
    }
  }, [selectedUserapp, fetchProfileConfig, jwtToken]);

  // Sincronitzar amb el canvi de ruta
  useEffect(() => {
    if (routeUserapp && routeUserapp.toLowerCase() !== selectedUserapp) {
      setSelectedUserapp(routeUserapp.toLowerCase());
    }
  }, [routeUserapp, selectedUserapp]);

  // Càlcul de canvis sense desar (dirty)
  const isDirty = useMemo(() => {
    if (!profileConfig || !initialConfig) return false;
    return JSON.stringify(profileConfig) !== JSON.stringify(initialConfig);
  }, [profileConfig, initialConfig]);

  // Detecció de filtres corruptes heretats del backend (enabled: true amb llista buida)
  const corruptedFilters = useMemo(() => {
    return findCorruptedEmptyFilters(profileConfig);
  }, [profileConfig]);
  const hasCorrupted = corruptedFilters.length > 0;

  // Canvi de perfil amb detecció de canvis sense desar
  const handleProfileChangeRequest = (newUserapp) => {
    if (newUserapp === selectedUserapp) return;

    if (isDirty) {
      setPendingUserappSwitch(newUserapp);
      setOpenUnsavedDialog(true);
    } else {
      applyProfileSwitch(newUserapp);
    }
  };

  const applyProfileSwitch = (newUserapp) => {
    setSelectedUserapp(newUserapp);
    if (setCtxApp) {
      setCtxApp((prev) => ({ ...prev, userapp: newUserapp }));
    }
    localStorage.setItem('selectedUserApp', newUserapp);
    navigate(`/${newUserapp}/sincroconfig`);
  };

  const handleConfirmDiscard = () => {
    if (pendingUserappSwitch) {
      applyProfileSwitch(pendingUserappSwitch);
      setPendingUserappSwitch(null);
    }
    setOpenUnsavedDialog(false);
  };

  // Toggle activació global del perfil
  const handleProfileEnabledToggle = (checked) => {
    setProfileConfig((prev) => ({
      ...prev,
      enabled: checked
    }));
  };

  // Guardar Canvis (PUT /api/v1/syncbanc/fdvconfig/{userapp})
  const handleSave = async () => {
    if (!selectedUserapp || !profileConfig) return;

    try {
      setIsSaving(true);
      // Sanejament rigorós del payload abans d'enviar al backend
      const sanitizedPayload = sanitizeSyncConfigPayload(profileConfig, initialConfig);

      const headers = jwtToken ? { Authorization: `${jwtToken}` } : {};
      const res = await axios.put(
        `${config.apiBaseUrl}/v1/syncbanc/fdvconfig/${selectedUserapp}`,
        sanitizedPayload,
        { headers }
      );

      const updated = res.data || sanitizedPayload;
      setProfileConfig(updated);
      setInitialConfig(JSON.parse(JSON.stringify(updated)));

      setSnackbar({
        open: true,
        message: `Configuració de "${selectedUserapp.toUpperCase()}" desada correctament!`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error al desar la configuració:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Error desconegut al desar';
      setSnackbar({
        open: true,
        message: `Error al desar: ${errorMsg}`,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Descartar canvis manuals i restaurar estat inicial
  const handleDiscardChanges = () => {
    if (initialConfig) {
      setProfileConfig(JSON.parse(JSON.stringify(initialConfig)));
      setSnackbar({
        open: true,
        message: 'S’han descartat els canvis no desats.',
        severity: 'info'
      });
    }
  };

  // Detecció d'elements eliminats entre initialConfig i profileConfig
  const getDeletedItems = useCallback(() => {
    if (!initialConfig || !profileConfig) return [];
    const deleted = [];

    // 1. Directes (mov-to-concepte)
    const initDirect = initialConfig.autoconcepte?.['mov-to-concepte'] || initialConfig.autoconcepte?.movToConcepte || {};
    const currDirect = profileConfig.autoconcepte?.['mov-to-concepte'] || profileConfig.autoconcepte?.movToConcepte || {};
    Object.keys(initDirect).forEach((k) => {
      if (!currDirect[k]) {
        const nom = initDirect[k]?.nom || k;
        deleted.push({ type: 'Grup Directe', key: k, label: `${nom} (mov-to-concepte)` });
      }
    });

    // 2. Patterns (mov-to-concepte-pattern)
    const initPattern = initialConfig.autoconcepte?.['mov-to-concepte-pattern'] || initialConfig.autoconcepte?.movToConceptePattern || {};
    const currPattern = profileConfig.autoconcepte?.['mov-to-concepte-pattern'] || profileConfig.autoconcepte?.movToConceptePattern || {};
    Object.keys(initPattern).forEach((k) => {
      if (!currPattern[k]) {
        const nom = initPattern[k]?.nom || k;
        deleted.push({ type: 'Regla Regex', key: k, label: `${nom} (mov-to-concepte-pattern)` });
      }
    });

    // 3. Regles avançades (regles-assignacio)
    const initRules = initialConfig.autoconcepte?.['regles-assignacio'] || initialConfig.autoconcepte?.reglesAssignacio || {};
    const currRules = profileConfig.autoconcepte?.['regles-assignacio'] || profileConfig.autoconcepte?.reglesAssignacio || {};
    Object.keys(initRules).forEach((k) => {
      if (!currRules[k]) {
        const nom = initRules[k]?.nom || k;
        deleted.push({ type: 'Regla Avançada', key: k, label: `${nom} (regles-assignacio)` });
      }
    });

    return deleted;
  }, [initialConfig, profileConfig]);

  // Estat per al diàleg de confirmació d'esborrat previ a la persistència
  const [deletedItemsToConfirm, setDeletedItemsToConfirm] = useState([]);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);

  // Click a Guardar Canvis: comprova si s'han eliminat regles o es desa directament
  const handleSaveClick = () => {
    const deleted = getDeletedItems();
    if (deleted.length > 0) {
      setDeletedItemsToConfirm(deleted);
      setOpenDeleteConfirmDialog(true);
    } else {
      handleSave();
    }
  };

  const handleConfirmSaveWithDeletions = () => {
    setOpenDeleteConfirmDialog(false);
    handleSave();
  };

  // Protecció contra tancament o recàrrega accidental si hi ha canvis sense desar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1.5, sm: 3 }, mb: 6, px: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
      {/* Barra superior de Perfil i Accions */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #e2e8f0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          {/* Títol i Selector de Perfil */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneIcon color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Configuració de Sincronització
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Regles funcionals de sincronització bancària, autoconcepte i alertes
                </Typography>
              </Box>
            </Box>

            {/* Dropdown de selecció de perfil */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, ml: { sm: 2 } }}>
              <InputLabel id="select-profile-label">Perfil / Usuari</InputLabel>
              <Select
                labelId="select-profile-label"
                value={selectedUserapp}
                label="Perfil / Usuari"
                onChange={(e) => handleProfileChangeRequest(e.target.value)}
                disabled={isLoadingTree || isLoadingProfile || !jwtToken}
              >
                {availableProfiles.map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.nom}
                      </Typography>
                      {!p.enabled && (
                        <Chip label="Inactiu" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Switch activació global del perfil */}
            {profileConfig && (
              <FormControlLabel
                control={
                  <Switch
                    checked={profileConfig.enabled ?? true}
                    onChange={(e) => handleProfileEnabledToggle(e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {profileConfig.enabled ? 'Perfil Actiu' : 'Perfil Desactivat'}
                  </Typography>
                }
              />
            )}
          </Box>

          {/* Botons d'acció */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              width: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'stretch', md: 'flex-start' }
            }}
          >
            {hasCorrupted && (
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Tooltip title="S'han detectat filtres buits actius que bloquegen la sincronització de moviments">
                  <Chip
                    icon={<WarningAmberIcon />}
                    label="Requereix sanejament"
                    color="error"
                    size="small"
                  />
                </Tooltip>
              </Box>
            )}

            {isDirty && !hasCorrupted && (
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Tooltip title="Tens canvis sense desar en aquest perfil">
                  <Chip
                    icon={<WarningAmberIcon />}
                    label="Canvis pendents"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              </Box>
            )}

            <Button
              variant="contained"
              color={hasCorrupted ? 'warning' : 'primary'}
              fullWidth={isMobile}
              startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveClick}
              disabled={(!isDirty && !hasCorrupted) || isSaving || isLoadingProfile || !jwtToken}
              size="medium"
              sx={{ fontWeight: 600, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 150 } }}
            >
              {isSaving ? 'Desant...' : hasCorrupted ? 'Sanejar i Desar' : 'Guardar Canvis'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Contingut Principal de la Configuració */}
      {(isLoadingProfile || !jwtToken) ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={48} />
          <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
            {!jwtToken
              ? 'Verificant sessió...'
              : `Carregant la configuració de ${(selectedUserapp || '').toUpperCase()}...`}
          </Typography>
        </Box>
      ) : !profileConfig ? (
        <Alert severity="warning">
          No s'ha pogut obtenir la configuració per al perfil {selectedUserapp}. Comprova la connexió amb el backend.
        </Alert>
      ) : (
        <Box>
          {/* Alerta de filtres buits actius heretats del backend */}
          {hasCorrupted && (
            <Alert
              severity="warning"
              sx={{ mb: 2.5, borderRadius: 2 }}
              action={
                <Button
                  color="warning"
                  size="small"
                  variant="contained"
                  onClick={handleSaveClick}
                  disabled={isSaving}
                  sx={{ fontWeight: 700, textTransform: 'none' }}
                >
                  Sanejar i Desar Ara
                </Button>
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Atenció: S'han detectat {corruptedFilters.length} filtre(s) buit(s) amb estat actiu (enabled: true)
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Aquesta situació (com filtres buits a comptes a incloure) fa que el backend descarti el 100% dels comptes. Fes clic a «Sanejar i Desar Ara» per desactivar-los automàticament i reprendre la sincronització.
              </Typography>
            </Alert>
          )}

          {/* Navegació: Select en mòbil / Tabs en escriptori */}
          {isMobile ? (
            <Paper elevation={1} sx={{ p: 1.5, mb: 2.5, borderRadius: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}>
              <FormControl fullWidth size="small">
                <InputLabel id="config-section-select-label">Secció de Configuració</InputLabel>
                <Select
                  labelId="config-section-select-label"
                  value={currentTab}
                  label="Secció de Configuració"
                  onChange={(e) => setCurrentTab(Number(e.target.value))}
                >
                  <MenuItem value={0}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceIcon color="primary" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Adaptadors</Typography>
                      </Box>
                      <Chip size="small" label={Object.keys(profileConfig.adapters || {}).length} sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  </MenuItem>
                  <MenuItem value={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoFixHighIcon color="primary" fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Autoconcepte</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsActiveIcon color="primary" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Alarmes</Typography>
                      </Box>
                      {profileConfig.alarmes?.enabled ? (
                        <Chip label="ON" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                      ) : (
                        <Chip label="OFF" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: 2, mb: 3 }}>
              <Tabs
                value={currentTab}
                onChange={(_, newVal) => setCurrentTab(newVal)}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab
                  icon={<AccountBalanceIcon />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Adaptadors</span>
                      <Chip
                        size="small"
                        label={Object.keys(profileConfig.adapters || {}).length}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  sx={{ fontWeight: 600, py: 1.5 }}
                />
                <Tab
                  icon={<AutoFixHighIcon />}
                  iconPosition="start"
                  label="Autoconcepte"
                  sx={{ fontWeight: 600, py: 1.5 }}
                />
                <Tab
                  icon={<NotificationsActiveIcon />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Alarmes</span>
                      {profileConfig.alarmes?.enabled ? (
                        <Chip label="ON" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                      ) : (
                        <Chip label="OFF" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  }
                  sx={{ fontWeight: 600, py: 1.5 }}
                />
              </Tabs>
            </Paper>
          )}

          {/* Tab 0: Adaptadors */}
          {currentTab === 0 && (
            <AdaptersSection
              adapters={profileConfig.adapters || {}}
              initialAdapters={initialConfig?.adapters || {}}
              onChange={(newAdapters) => {
                setProfileConfig((prev) => ({
                  ...prev,
                  adapters: newAdapters
                }));
              }}
              disabled={isSaving}
            />
          )}

          {/* Tab 1: Autoconcepte */}
          {currentTab === 1 && (
            <AutoconcepteSection
              autoconcepte={profileConfig.autoconcepte || {}}
              onChange={(newAutoconcepte) => {
                setProfileConfig((prev) => ({
                  ...prev,
                  autoconcepte: newAutoconcepte
                }));
              }}
              conceptesList={conceptesList}
              disabled={isSaving}
              userapp={selectedUserapp}
              onNotification={(message, severity) => {
                setSnackbar({
                  open: true,
                  message,
                  severity: severity || 'info'
                });
              }}
            />
          )}

          {/* Tab 2: Alarmes */}
          {currentTab === 2 && (
            <AlarmesSection
              alarmes={profileConfig.alarmes || {}}
              onChange={(newAlarmes) => {
                setProfileConfig((prev) => ({
                  ...prev,
                  alarmes: newAlarmes
                }));
              }}
              conceptesList={conceptesList}
              disabled={isSaving}
            />
          )}
        </Box>
      )}

      {/* Diàleg d'avís de Canvis Sense Desar en canviar de perfil */}
      <Dialog
        open={openUnsavedDialog}
        onClose={() => setOpenUnsavedDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmberIcon color="warning" /> Canvis sense desar
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tens canvis sense desar en el perfil actual. Si canvies de perfil ara, es perdran les modificacions no guardades. Vols continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenUnsavedDialog(false)}>Romandre aquí</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmDiscard}>
            Descartar i canviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Barra Flotant Inferior Fixa de Canvis Pendents (Visible en tot moment mitjançant Portal) */}
      {isDirty && (
        <Portal>
          <Paper
            elevation={16}
            sx={{
              position: 'fixed',
              bottom: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', md: 24 },
              left: { xs: 12, md: '50%' },
              right: { xs: 12, md: 'auto' },
              width: { xs: 'calc(100% - 24px)', md: 'auto' },
              transform: { xs: 'none', md: 'translateX(-50%)' },
              zIndex: 2500,
              maxWidth: { md: 680 },
              p: { xs: 1.5, md: 1.5 },
              borderRadius: 3,
              backgroundColor: '#1e293b',
              color: '#ffffff',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(245, 158, 11, 0.5)',
              boxSizing: 'border-box'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 1.25, md: 2 }
              }}
            >
              {/* Títol / Avís */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 22, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#f8fafc', fontSize: '0.85rem', lineHeight: 1.2 }} noWrap>
                    Tens canvis pendents de desar
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', display: { xs: 'none', md: 'block' } }} noWrap>
                    Perfil: <strong style={{ color: '#cbd5e1' }}>{selectedUserapp.toUpperCase()}</strong> (sense persistir a la BBDD)
                  </Typography>
                </Box>
              </Box>

              {/* Botons d'acció */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  gap: 1,
                  width: { xs: '100%', md: 'auto' }
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveClick}
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
                  sx={{
                    backgroundColor: '#2563eb',
                    fontWeight: 700,
                    py: { xs: 0.9, md: 0.75 },
                    fontSize: { xs: '0.85rem', md: '0.8125rem' },
                    whiteSpace: 'nowrap',
                    width: { xs: '100%', md: 'auto' },
                    order: { xs: 1, md: 2 },
                    '&:hover': { backgroundColor: '#1d4ed8' }
                  }}
                >
                  {isSaving ? 'Desant...' : 'Guardar Canvis'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDiscardChanges}
                  disabled={isSaving}
                  sx={{
                    color: '#cbd5e1',
                    borderColor: '#475569',
                    fontWeight: 600,
                    py: { xs: 0.6, md: 0.75 },
                    fontSize: { xs: '0.8rem', md: '0.8125rem' },
                    width: { xs: '100%', md: 'auto' },
                    order: { xs: 2, md: 1 },
                    '&:hover': { borderColor: '#94a3b8', backgroundColor: 'rgba(255,255,255,0.05)' }
                  }}
                >
                  Descartar
                </Button>
              </Box>
            </Box>
          </Paper>
        </Portal>
      )}

      {/* Diàleg de Confirmació d'Eliminació abans de persistir a BBDD */}
      <Dialog
        open={openDeleteConfirmDialog}
        onClose={() => setOpenDeleteConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteOutlineIcon color="error" /> Confirmar eliminació de regles a la BBDD
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Esteu a punt de desar la configuració a la base de dades i s'eliminaran definitivament els següents elements del perfil <strong>{selectedUserapp.toUpperCase()}</strong>:
          </DialogContentText>
          <Box sx={{ bgcolor: '#fff1f2', p: 1.5, borderRadius: 2, border: '1px solid #fecdd3', mb: 2, maxHeight: 200, overflowY: 'auto' }}>
            {deletedItemsToConfirm.map((item, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Chip label={item.type} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#9f1239' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
          <DialogContentText variant="caption" color="text.secondary">
            Aquesta acció és irreversible un cop guardada al servidor. Si no voleu eliminar aquests elements, premeu Cancel·lar o Descartar canvis.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteConfirmDialog(false)} color="inherit">
            Cancel·lar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmSaveWithDeletions}
            startIcon={<DeleteOutlineIcon />}
          >
            Confirmar i Desar a la BBDD
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SincroConfigPage;
